import type { z } from "zod";
import { api } from "@/lib/api";
import type {
  checkRecoveryTokenSchema,
  loginSchema,
  recoverPasswordSchema,
  resetPasswordSchema,
} from "@/schemas/auth.schema";
import type {
  CheckRecoveryTokenResponse,
  LoginResponse,
  LogoutResponse,
  RecoverPasswordResponse,
  ResetPasswordResponse,
  ValidateInviteResponse,
} from "@/types/auth";

export async function loginService(
  data: z.infer<typeof loginSchema>,
): Promise<LoginResponse> {
  const formData = new URLSearchParams();

  formData.append("login", data.login);
  formData.append("password", data.password);

  if (data.captcha) formData.append("captcha", data.captcha);

  return (
    await api.post("/api/login", formData, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
  ).data;
}

export async function logoutService(): Promise<LogoutResponse> {
  return (await api.get("/api/logout")).data;
}

export async function recoverPasswordService(
  data: z.infer<typeof recoverPasswordSchema>,
): Promise<RecoverPasswordResponse> {
  const formData = new URLSearchParams();

  formData.append("login", data.login);

  return (
    await api.post("/api/generaterecoverytoken", formData, {
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      withCredentials: true,
    })
  ).data;
}

export async function checkRecoveryTokenService(
  data: z.infer<typeof checkRecoveryTokenSchema>,
): Promise<CheckRecoveryTokenResponse> {
  // O código validado libera a troca de senha na sessão da API (cookie)
  return (
    await api.post("/api/checkrecoverytoken", data, { withCredentials: true })
  ).data;
}

export async function resetPasswordService(
  data: z.infer<typeof resetPasswordSchema>,
): Promise<ResetPasswordResponse> {
  // Usa a sessão de recuperação liberada em checkRecoveryTokenService
  return (
    await api.put("/api/user/changepassword", data, { withCredentials: true })
  ).data;
}

/** Confere o link de convite de novo usuário (token recebido por e-mail). */
export async function validateInviteService(
  token: string,
): Promise<ValidateInviteResponse> {
  return (await api.post("/api/invite/validate", { token })).data;
}

/** Cria a senha do convidado e ativa a conta. */
export async function acceptInviteService(data: {
  token: string;
  password: string;
}): Promise<{ success: boolean }> {
  return (await api.post("/api/invite/accept", data)).data;
}
